import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { Field, FieldProps, Form, Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useParams } from "react-router-dom";
import { useUpdateStateWithdrawalMutation } from "store/apis/entity";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import * as Yup from "yup";

type WithdrawJurisdictionValues = {
  withdrawalDate: string | null;
};

const validationSchema = Yup.object({
  withdrawalDate: Yup.string()
    .nullable()
    .required("Withdrawal date is required"),
});

const initialValues: WithdrawJurisdictionValues = { withdrawalDate: null };

const WithdrawJurisdiction = ({
  close,
  isOpen,
  stateId,
  entityId: entityIdFromProps,
  isWithdrawn = false,
}: ModalProps & {
  stateId: string;
  entityId?: string;
  isWithdrawn?: boolean;
}) => {
  const { successToast, alertToast } = useToast();
  const { entityId: entityIdFromParams } = useParams<{ entityId: string }>();
  const entityId = entityIdFromParams || entityIdFromProps || "";
  const { uuid: groupId } = useCurrentGroupContext();
  const [updateStateWithdrawal, { isLoading }] =
    useUpdateStateWithdrawalMutation();

  const onUnwithdraw = async () => {
    try {
      await updateStateWithdrawal({
        groupId,
        entityId,
        stateId,
        withdrawalDate: null,
      }).unwrap();
      successToast({ message: "Jurisdiction reinstated!" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onSubmit = async (values: WithdrawJurisdictionValues) => {
    try {
      await updateStateWithdrawal({
        groupId,
        entityId,
        stateId,
        withdrawalDate: values.withdrawalDate,
      }).unwrap();
      successToast({ message: "Jurisdiction withdrawn!" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  if (isWithdrawn) {
    return (
      <Modal.Root onOpenChange={close} open={isOpen}>
        <Modal.Content size="small">
          <Modal.Header>
            <Modal.Title>Reinstate Jurisdiction</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-text-body-sm">
              Are you sure you want to reinstate this jurisdiction? Future
              deadlines for this state will become active again
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button onClick={close} disabled={isLoading}>
                Cancel
              </Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={onUnwithdraw}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Reinstate
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    );
  }

  return (
    <Modal.Root onOpenChange={close} open={isOpen}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ submitForm, isSubmitting }) => (
          <Modal.Content useCustomOverlay>
            <Modal.Header>
              <Modal.Title>Withdraw Jurisdiction</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <div className="t-flex t-flex-col t-gap-4">
                <div className="t-text-body-sm">
                  All deadlines for this state will become inactive from the
                  withdrawal date onwards.
                </div>
                <Form>
                  <Field name="withdrawalDate">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        label="Withdrawal date"
                        required
                        portalId="withdraw-jurisdiction-date"
                      />
                    )}
                  </Field>
                </Form>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button onClick={close} disabled={isSubmitting || isLoading}>
                  Cancel
                </Button>
              </Modal.RawClose>
              <Button
                customType="danger"
                onClick={submitForm}
                disabled={isSubmitting || isLoading}
                isLoading={isSubmitting || isLoading}
              >
                Withdraw
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        )}
      </Formik>
    </Modal.Root>
  );
};

export default WithdrawJurisdiction;
